// ============================================
// pages/Salon/SalonMarketing.js
// AI-powered marketing poster generator for salons
// ============================================
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import {
  Sparkles, Download, Share2, RotateCcw, Loader2,
  Image as ImageIcon, Wand2, Building2, Phone, Mail,
  AlertCircle, CheckCircle2, Copy
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const publicAsset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const presetPosters = {
  'poster-1': {
    id: 'poster-1',
    name: 'Grand Opening',
    description: 'Picture 1 - Grand opening salon poster.',
    src: publicAsset('/too/1.jpg'),
  },
  'poster-2': {
    id: 'poster-2',
    name: "Gent's Salon",
    description: 'Picture 2 - Grooming packages poster.',
    src: publicAsset('/too/2.png'),
  },
  'poster-3': {
    id: 'poster-3',
    name: 'Glamour Haven',
    description: 'Picture 3 - 40% OFF poster.',
    src: publicAsset('/too/3.png'),
  },
  'poster-4': {
    id: 'poster-4',
    name: 'Girls Parlor',
    description: 'Picture 4 - Long hair poster.',
    src: publicAsset('/too/4.jpg'),
  },
  'poster-5': {
    id: 'poster-5',
    name: 'Revive & Renew',
    description: 'Picture 5 - Facial treatment poster.',
    src: publicAsset('/too/5.png'),
  },
};

const promptPosterMap = [
  {
    prompt: 'Get 20% OFF on your first salon visit + FREE treatment with any service',
    posterId: 'poster-1',
  },
  {
    prompt: '20% OFF on premium grooming packages this month',
    posterId: 'poster-2',
  },
  {
    prompt: 'Glamour Haven Special: Get 40% OFF on hair styling, color & beauty services',
    posterId: 'poster-3',
  },
  {
    prompt: "Get the long hair you've always wanted - exclusive girls salon offers available",
    posterId: 'poster-4',
  },
  {
    prompt: 'Revive & Renew: Facials, Peels & LED Therapy - Book your treatment today',
    posterId: 'poster-5',
  },
];

export default function SalonMarketing() {
  const { userToken: token } = useAuth();
  const generationTimerRef = useRef(null);

  // Salon info
  const [salonInfo, setSalonInfo] = useState({
    salonName: '',
    contactPersonName: '',
    contactPhone: '',
  });

  // Poster generation
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImageBackup, setGeneratedImageBackup] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedPresetPoster, setSelectedPresetPoster] = useState(null);

  const activePoster = generatedImage || selectedPresetPoster?.src || null;

  useEffect(() => {
    return () => {
      if (generationTimerRef.current) {
        clearTimeout(generationTimerRef.current);
      }
    };
  }, []);

  // Load salon info
  useEffect(() => {
    const loadSalonInfo = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${SALON_API_URL}/admin-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.data?.salonProfile) {
          setSalonInfo({
            salonName: data.data.salonProfile.salonName || 'My Salon',
            contactPersonName: data.data.salonProfile.contactPersonName || '',
            contactPhone: data.data.salonProfile.contactPhone || '',
          });
        }
      } catch (err) {
        console.error('Failed to load salon info:', err);
      }
    };
    loadSalonInfo();
  }, [token]);

  const clearGenerationTimer = () => {
    if (generationTimerRef.current) {
      clearTimeout(generationTimerRef.current);
      generationTimerRef.current = null;
    }
  };

  const beginPresetGeneration = (presetId, promptText) => {
    const poster = presetPosters[presetId];
    if (!poster) return;

    clearGenerationTimer();
    setPrompt(promptText);
    setIsGenerating(true);
    setError('');
    setGeneratedImage(null);
    setGeneratedImageBackup(null);
    setSelectedPresetPoster(null);

    const delay = 10000 + Math.floor(Math.random() * 10001);
    generationTimerRef.current = setTimeout(() => {
      setSelectedPresetPoster(poster);
      setGeneratedImage(null);
      setGeneratedImageBackup(null);
      setIsGenerating(false);
      toast.success('Poster generated successfully!');
      generationTimerRef.current = null;
    }, delay);
  };

  // Generate poster
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your poster');
      return;
    }

    clearGenerationTimer();
    setIsGenerating(true);
    setError('');
    setGeneratedImage(null);
    setGeneratedImageBackup(null);
    setSelectedPresetPoster(null);

    try {
      const res = await fetch(`${SALON_API_URL}/marketing/generate-poster`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: prompt,
          salonName: salonInfo.salonName,
          contactPhone: salonInfo.contactPhone
        })
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to generate poster');
      }

      setGeneratedImage(data.data.imageUrl);
      setGeneratedImageBackup(data.data.remoteUrl || null);
      toast.success('Poster generated successfully!');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download poster
  const handleDownload = async () => {
    if (!activePoster) return;

    try {
      const response = await fetch(activePoster);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${salonInfo.salonName.replace(/\s+/g, '_')}_poster_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Poster downloaded!');
    } catch (err) {
      toast.error('Failed to download poster');
    }
  };

  // Share on WhatsApp
  const handleWhatsAppShare = () => {
    if (!activePoster) return;

    const message = `Check out our latest offer at ${salonInfo.salonName}! ${salonInfo.contactPhone ? `Contact: ${salonInfo.contactPhone}` : ''}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message + '\n\n' + activePoster)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Copy image URL
  const handleCopyUrl = () => {
    if (!activePoster) return;
    navigator.clipboard.writeText(activePoster);
    toast.success('Image URL copied to clipboard!');
  };

  // Retry generation
  const handleRetry = () => {
    clearGenerationTimer();
    setGeneratedImage(null);
    setGeneratedImageBackup(null);
    setSelectedPresetPoster(null);
    setIsGenerating(false);
    setError('');
  };


  // Example prompts
  const examplePrompts = promptPosterMap.map((item) => item.prompt);

  const applyExamplePrompt = (example) => {
    const mapped = promptPosterMap.find((item) => item.prompt === example);
    if (mapped) {
      beginPresetGeneration(mapped.posterId, example);
      return;
    }
    setPrompt(example);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 md:p-8">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl">
              <Wand2 className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                AI Marketing Studio
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Create stunning promotional posters with AI
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Input */}
          <div className="space-y-6">
            {/* Salon Info Card */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <Building2 size={20} className="text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Salon Information
                </h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                  <Building2 size={18} className="text-purple-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">
                      Salon Name
                    </p>
                    <p className="text-base font-bold text-gray-900 dark:text-white">
                      {salonInfo.salonName || 'Not set'}
                    </p>
                  </div>
                </div>

                {salonInfo.contactPersonName && (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <Mail size={18} className="text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">
                        Contact Person
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {salonInfo.contactPersonName}
                      </p>
                    </div>
                  </div>
                )}

                {salonInfo.contactPhone && (
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                    <Phone size={18} className="text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">
                        Contact Number
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {salonInfo.contactPhone}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 italic">
                This information will be automatically included in your posters
              </p>
            </div>

            {/* Prompt Input */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={20} className="text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Describe Your Offer
                </h2>
              </div>

              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Example: 40% off on hair styling and hair coloring services with straightening"
                className="w-full h-32 px-4 py-3 bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 text-gray-800 dark:text-white resize-none"
                disabled={isGenerating}
              />

              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Quick Examples
                </p>
                <div className="space-y-2">
                  {examplePrompts.map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => applyExamplePrompt(example)}
                      className="w-full text-left px-3 py-2 text-xs bg-gray-50 dark:bg-slate-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 transition-colors"
                      disabled={isGenerating}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                className="w-full mt-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Generating Magic...
                  </>
                ) : (
                  <>
                    <Wand2 size={20} />
                    Generate Poster
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon size={20} className="text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Poster Preview
                </h2>
              </div>

              {/* Preview Area */}
              <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-700 dark:to-slate-600 rounded-xl overflow-hidden flex items-center justify-center">
                {isGenerating ? (
                  <div className="text-center">
                    <Loader2 size={48} className="animate-spin text-purple-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-300 font-semibold">
                      Creating your poster...
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      This may take 10-20 seconds
                    </p>
                  </div>
                ) : activePoster ? (
                  <img
                    src={activePoster}
                    alt="Selected Poster"
                    className="w-full h-full object-contain"
                    onError={() => {
                      if (generatedImageBackup && generatedImage !== generatedImageBackup) {
                        setGeneratedImage(generatedImageBackup);
                        return;
                      }
                      if (generatedImage) {
                        setError('Image provider returned an unusable image. Try again with another prompt.');
                        setGeneratedImage(null);
                        return;
                      }
                      setError('Could not load the selected poster template.');
                      setSelectedPresetPoster(null);
                    }}
                  />
                ) : error ? (
                  <div className="text-center p-6">
                    <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
                    <p className="text-red-600 dark:text-red-400 font-semibold mb-2">
                      Generation Failed
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {error}
                    </p>
                  </div>
                ) : (
                  <div className="text-center p-6">
                    <ImageIcon size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 font-semibold">
                      Your poster will appear here
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Enter a description or tap a quick example
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {activePoster && (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    onClick={handleRetry}
                    className="py-3 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    <RotateCcw size={18} />
                    Retry
                  </button>

                  <button
                    onClick={handleDownload}
                    className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    <Download size={18} />
                    Download
                  </button>

                  <button
                    onClick={handleWhatsAppShare}
                    className="py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    <Share2 size={18} />
                    WhatsApp
                  </button>

                  <button
                    onClick={handleCopyUrl}
                    className="py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    <Copy size={18} />
                    Copy URL
                  </button>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    Tips for Better Posters
                  </h3>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    <li>• Be specific about the offer (discount %, services)</li>
                    <li>• Mention the occasion (weekend, festival, season)</li>
                    <li>• Keep it simple and clear</li>
                    <li>• Include urgency (limited time, this week only)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
